import { Route, Routes, Navigate } from 'react-router-dom';
import classnames from 'classnames/bind';

import { useHasAccess } from 'hooks/useHasAccess';

import { AccessTokens } from 'settings/views/AccessTokens/AccessTokens';
import { AccountSettings } from 'settings/views/Account/Account';
import { Metrics } from 'settings/views/Metrics/Metrics';
import { NotificationChannels } from 'settings/views/NotificationChannels/NotificationChannels';
import { NotificationRules } from 'settings/views/NotificationRules/NotificationRules';
import { Organizations } from 'organizations/views';
import { PasswordSettings } from 'settings/views/Password/Password';
import { RequireAuth } from 'components/RequireAuth/RequireAuth';
import { UniversalBridge } from 'settings/views/UniversalBridge/UniversalBridge';
import { UserGroups } from 'settings/views/UserGroups/UserGroups';
import { WorkspaceUtilization } from 'settings/views/WorkspaceUtilization/WorkspaceUtilization';

import { SettingsSidebar } from './Sidebar';
import styles from './Settings.module.scss';

const c = classnames.bind(styles);

export function Settings() {
  const redirectPath = useRedirectPath();

  return (
    <div className={c('settings')}>
      <SettingsSidebar />

      <Routes>
        <Route path="/" element={<Navigate to={redirectPath} replace />} />

        <Route
          path="access-tokens"
          element={
            <RequireAuth section="access_tokens">
              <AccessTokens />
            </RequireAuth>
          }
        />
        <Route
          path="universal-bridge"
          element={
            <RequireAuth section="universal_bridge">
              <UniversalBridge />
            </RequireAuth>
          }
        />
        <Route
          path="metrics"
          element={
            <RequireAuth section="metrics">
              <Metrics />
            </RequireAuth>
          }
        />
        <Route
          path="workspace-utilization"
          element={
            <RequireAuth section="workspace_utilization">
              <WorkspaceUtilization />
            </RequireAuth>
          }
        />
        <Route
          path="notification-rules"
          element={
            <RequireAuth section="notification_rules">
              <NotificationRules />
            </RequireAuth>
          }
        />
        <Route
          path="groups"
          element={
            <RequireAuth section="notification_rules">
              <UserGroups />
            </RequireAuth>
          }
        />
        <Route
          path="notification-channels"
          element={<NotificationChannels />}
        />
        <Route path="organization/*" element={<Organizations />} />
        <Route path="account" element={<AccountSettings />} />
        <Route path="password" element={<PasswordSettings />} />
      </Routes>
    </div>
  );
}

function useRedirectPath() {
  const [hasAccess] = useHasAccess();

  // Owners, managers, and billing land on the organization utilization view.
  if (hasAccess('organization_utilization')) {
    return 'organization';
  }

  // Deployers land on the workspace utilization view.
  if (hasAccess('workspace_utilization')) {
    return 'workspace-utilization';
  }

  if (hasAccess('access_tokens')) {
    return 'access-tokens';
  }

  if (hasAccess('universal_bridge')) {
    return 'universal-bridge';
  }

  if (hasAccess('notification_rules')) {
    return 'groups';
  }

  return 'account';
}
